import { FC, useState } from 'react';
import { Grid, Box, Typography, useMediaQuery, Theme, Button } from '@mui/material';
import { AudioPlayer, FileUpload, MuiIconButton, RichText } from '@/components';
import VideoItem from '@/screens/Memories/components/VideoContent/VideoItem';
import Image from 'next/image';
import MediaPopup from '@/components/MediaPopup';
import TextArea from '@/components/TextArea';
import { getFileType } from '@/utils';
import { openModal } from '@/store/actions';
import { useDispatch } from 'react-redux';
import MuiTextField3 from '@/components/TextField3';
import InspirationModal from '@/components/InspirationalModal';

interface MediaUploadStepProps {
  values: any;
  errors: any;
  touched: any;
  handleOnTouched: any;
  handleChange: (e: any) => void;
  formikSubmit?: () => void;
  setFieldValue: (name: string, value: any) => void;
}

const MediaUploadStep: FC<MediaUploadStepProps> = ({
  errors,
  values,
  handleOnTouched,
  touched,
  handleChange,
  setFieldValue,
  formikSubmit,
}) => {
  const [showRichText, setShowRichText] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [showCard, setShowCard] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{ media: any; type: string; index: any } | null>(null);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const handleOpen = (media: any, index: any) => {
    setSelectedMedia({ media, type: media?.file?.type, index });
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedMedia(null);
    setOpen(false);
  };

  const validateMediaFile = (file: File, type: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (type === 'image') {
        const img = document.createElement('img') as HTMLImageElement;
        img.src = URL.createObjectURL(file);
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
      } else if (type === 'video') {
        const media = document.createElement(type) as HTMLMediaElement;
        media.src = URL.createObjectURL(file);
        media.onloadeddata = () => resolve(true);
        media.onerror = () => resolve(false);
      } else {
        resolve(false);
      }
    });
  };

  const handleFileUpload = async (_: any, files: File | FileList) => {
    if (!files) {
      // alert('No files provided.');
      return;
    }

    const inputFiles = files instanceof File ? [files] : Array.from(files);
    // alert(`Received ${inputFiles.length} file(s).`);

    const validFiles: any[] = [];
    const previews: string[] = [];

    const alreadyHasFeaturedImage = values.mediaFiles.some((media: any) => {
      const isImage =
        media.file?.type?.startsWith('image') ||
        (typeof media.url === 'string' && /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(media.url)) ||
        (typeof media.preview === 'string' && /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(media.preview));
      return media.isFeatured && isImage;
    });

    let featuredAssigned = alreadyHasFeaturedImage;

    for (const file of inputFiles) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      let mime = file.type;

      if (!mime || mime === '') {
        if (['mp3', 'wav', 'aac', 'm4a', 'ogg'].includes(extension)) {
          mime = `audio/${extension}`;
        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(extension)) {
          mime = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
        } else if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(extension)) {
          mime = `video/${extension}`;
        }
      }

      // alert(`Processing file: ${file.name}\nMIME: ${mime}`);

      const isImage = mime.startsWith('image');
      const isVideo = mime.startsWith('video');
      const isAudio = mime.startsWith('audio');

      let fileType = '';
      if (isImage) fileType = 'image';
      else if (isVideo) fileType = 'video';
      else if (isAudio) fileType = 'audio';

      if (!fileType) {
        // alert(`Unsupported file type: ${file.name}`);
        console.warn(`Unsupported file:`, file);
        continue;
      }

      // // alert(`Detected type: ${fileType}`);

      const shouldBeFeatured = isImage && !featuredAssigned;
      if (shouldBeFeatured) featuredAssigned = true;

      const previewURL = URL.createObjectURL(file);
      // // alert(`Preview URL created for ${file.name}`);

      validFiles.push({
        file,
        preview: previewURL,
        isFeatured: shouldBeFeatured,
      });

      previews.push(previewURL);
    }

    if (validFiles.length > 0) {
      // alert(`Uploading ${validFiles.length} valid file(s).`);
      setPreviewUrls((prev) => [...prev, ...previews]);
      setFieldValue('mediaFiles', [...values.mediaFiles, ...validFiles]);
    } else {
      // alert('No valid files to upload.');
    }
  };

  const handleDeleteMedia = (media: any) => {
    const updatedMediaFiles = values.mediaFiles.filter((_: any, i: any) => _?.preview != media?.preview);

    setFieldValue('mediaFiles', updatedMediaFiles);
  };

  const hasUploadedMedia = values.mediaFiles && values.mediaFiles.length > 0;
  const [featuredIndex, setFeaturedIndex] = useState<number | null>(null);

  const handleFeaturedSelect = (media: any) => {
    const updatedMediaFiles = values.mediaFiles.map((item: any) => ({
      ...item,
      isFeatured: item.preview === media.preview ? !item.isFeatured : false,
    }));

    setFieldValue('mediaFiles', updatedMediaFiles);

    setFeaturedIndex(media.isFeatured ? null : media.preview);
  };
  const isAudioFile = (media: any) => {
    const type = media?.file?.type || '';
    const url = media?.url || media?.preview || '';
    const extension = url.split('.').pop()?.toLowerCase();

    const audioExtensions = [
      'mp3',
      'wav',
      'ogg',
      'm4a',
      'aac',
      'flac',
      'aiff',
      'alac',
      'amr',
      'wma',
      '3gp',
      'webm',
      'opus',
    ];

    return type.startsWith('audio') || (extension && audioExtensions.includes(extension));
  };
  // const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const handleClick = () => {
    setShowCard((prev) => !prev);
  };

  return (
    <Box display='flex' flexDirection='column' alignItems='center' width='100%' gap={1} mb={1} marginLeft={'0.85rem'}>
      {hasUploadedMedia && (
        <>
          <Box display='flex' justifyContent={isMobile ? 'center' : 'space-between'} alignItems='center' gap={'0.6rem'}>
            {/* Photos & Videos Upload */}
            <Box display='flex' justifyContent='center'>
              <FileUpload
                height='100px'
                name='photosAndVideos'
                multiple
                onChange={handleFileUpload}
                buttonLayout={true}
                placeholder='Photos & Videos'
                acceptedFormats='image/*,video/*'
                icon='/icons/upload'
                buttonwidth='170px'
              />
            </Box>

            {/* Audio Upload */}
            <Box display='flex' justifyContent='center'>
              <FileUpload
                height='100px'
                name='audio'
                mediaType='audio'
                onChange={handleFileUpload}
                buttonLayout={true}
                placeholder='Audio'
                acceptedFormats='audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.aiff,.alac,.amr,.wma,.3gp,.webm,.opus,.mp4,audio/mpeg,audio/x-m4a,audio/mp4,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/flac,audio/aiff,audio/x-aiff,audio/alac,audio/amr,audio/x-ms-wma,audio/3gpp,audio/webm,audio/opus'
                icon='/icons/audio-file'
                buttonwidth='85px'
              />
            </Box>

            {/* Additional Action Button */}
            {/* <Box display='flex' justifyContent='center'>
              <MuiIconButton
                icon='/icons/text-file'
                label='Text'
                altIcon='text-file'
                iconPosition='left'
                direction='row'
                background='white'
                labelColor='black'
                width={71}
                isRounded={false}
                height={40}
                iconHeight={20}
                iconWidth={20}
                sx={{
                  borderRadius: '24px',
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                }}
                method={() => setShowRichText((prev) => !prev)}
              />
            </Box> */}
          </Box>
        </>
      )}

      <Box display='flex' flexDirection='column' alignItems='center' mt={2}>
        <Typography fontSize='13px' color='#FEFEFE' mb={'3px'} lineHeight={'18px'} align='left' sx={{ width: isMobile ?'310px':'350px', fontFamily: '"Inter", sans-serif !important' }}>
          Name this memory
        </Typography>
        <MuiTextField3
          id='title'
          name='title'
          onBlur={() => handleOnTouched('title')}
          onChange={handleChange}
          value={values.title}
          placeholder='Title'
          
          errorMessage={touched.title ? errors.title : ''}
          error={touched.title && !!errors.title}
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '46px',
              borderRadius: '5px',
              background: '#fff',
              '& fieldset': {
                border: 'none',
              },
              '& .MuiOutlinedInput-input': {
                padding: 0,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '15px',
              },
            },
            width:isMobile ? '310px' :'350px',
          }}
        />
      </Box>

      <Box display='flex' flexDirection={'column'} justifyContent='center' mb={1}>
<Typography
  fontSize="1rem"
  mb={1}
  color="#fff"
  align="center"
  onClick={handleClick}
  sx={{
    cursor: 'pointer',
    textDecoration: 'underline',

  }}
>
  Need Inspiration?
</Typography>

        <InspirationModal showCard={showCard} setShowCard={setShowCard} />
      </Box>

      {!hasUploadedMedia && (
        <>
          <Box display='flex' justifyContent='center' width='100%' mb={1}>
            <FileUpload
              height='100px'
              name='mediaFiles'
              buttonLayout={false}
              multiple
              onChange={handleFileUpload}
              placeholder='Upload Photos & Videos'
              icon='/icons/upload-white.svg'
              acceptedFormats='image/*,video/*'
            />
          </Box>

          <Box display='flex' justifyContent='center' width='100%' mb={'12px'}>
            <FileUpload
              height='100px'
              name='audio'
              buttonLayout={false}
              mediaType='audio'
              multiple
              onChange={handleFileUpload}
              placeholder='Upload or Record Audio'
              icon='/icons/sound-light.svg'
              acceptedFormats='audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.aiff,.alac,.amr,.wma,.3gp,.webm,.opus,.mp4,audio/mpeg,audio/x-m4a,audio/mp4,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/flac,audio/aiff,audio/x-aiff,audio/alac,audio/amr,audio/x-ms-wma,audio/3gpp,audio/webm,audio/opus'
            />
          </Box>

          {/* Write a Text Memory Button (Only If No Media Exists) */}
          {/* <Box display='flex' justifyContent='center' width='100%'>
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              borderRadius='10px'
              tabIndex={0}
              overflow='hidden'
              onClick={() => setShowRichText((prev) => !prev)}
              position='relative'
              width='100%'
              maxWidth={isMobile ? '320px' : '350px'}
              sx={{ backgroundColor: 'rgba(254, 254, 254, 0.25)', cursor: 'pointer' }}
              height='100px'
              border='1px dashed rgba(217, 217, 217, 1)'>
              <Typography
                textAlign='center'
                variant={isMobile ? 'subtitle1' : 'h4'}
                fontSize='20px'
                color='rgba(255, 255, 255, 1)'
                width='100%'
                display='flex'
                alignItems='center'
                justifyContent='center'>
                Write a text memory
                <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                  <Image src='/icons/text-file-white.svg' alt='write icon' width='39' height='39' />
                </Box>
              </Typography>
            </Box>
          </Box> */}
        </>
      )}

      {/* Media Preview Section */}
      <Grid
        container
        width={isMobile ? '350px' : '92%'}
        display={'flex'}
        spacing={isMobile ? 1 : 2}
        columnSpacing={isMobile ? 0 : 2}
        sx={{ marginright: '1rem' }}>
        {/* Render Images & Videos */}
        {Array.isArray(values.mediaFiles) &&
          values.mediaFiles.length > 0 &&
          values.mediaFiles
            .filter((media: any) => {
              const type = media?.file?.type || '';
              return (
                type.startsWith('image') ||
                type.startsWith('video') ||
                (media?.url && getFileType(media?.url) == 'image') ||
                (media?.url && getFileType(media?.url) == 'video')
              );
            })
            .map((media: any, index: number) => (
              <Grid
                item
                xs={media?.file?.type?.startsWith('audio') ? 12 : 6}
                sm={media?.file?.type?.startsWith('audio') ? 6 : 4}
                md={media?.file?.type?.startsWith('audio') ? 6 : 3}
                key={`media-${media?.file?.name || media?.url || index || media?.preview}`}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',

                    height: '210px',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    marginBottom: '10px',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleOpen(media, index)}>
                  {media?.file?.type?.startsWith('video') || (media?.url && getFileType(media?.url) == 'video') ? (
                    <VideoItem
                      hideControls={true}
                      item={media?.url ? media?.url : media?.preview}
                      name={`video-${index}`}
                      elementSelected={null}
                      handleSelectVideo={() => {}}
                      thumbnail={false}
                    />
                  ) : (
                    (media?.file?.type?.startsWith('image') || (media?.url && getFileType(media?.url) == 'image')) && (
                      <img
                        src={media?.preview ? media?.preview : media?.file?.preview}
                        alt='Uploaded'
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                      />
                    )
                  )}
                  {media?.isFeatured && (
                    <Button
                      variant={media.isFeatured ? 'contained' : 'outlined'}
                      size='small'
                      onClick={() => handleFeaturedSelect(media)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: media.isFeatured ? 'primary.main' : 'rgba(0,0,0,0.5)',
                        color: '#000',
                        fontSize: '10px',
                        padding: '2px 6px',
                        minWidth: 'auto',
                        borderRadius: '12px',
                      }}>
                      {media.isFeatured ? '✓ Featured' : 'Feature'}
                    </Button>
                  )}
                </Box>
              </Grid>
            ))}
        {values.mediaFiles?.filter(isAudioFile).map((file: any, index: number) => (
          <Grid item xs={12} sm={6} md={6} key={`audio-${index}`} sx={{ display: 'flex' }}>
            <AudioPlayer
              key={index}
              showDownload={true}
              audioData={file?.preview || file?.url}
              name={file?.file?.name || file?.url.split('/').pop().split('_').pop()}
              handleDeleteMedia={() => handleDeleteMedia(file)}
            />
          </Grid>
        ))}
      </Grid>
      <Grid item xs={12}>
        <TextArea
          name='richText'
          placeholder='What makes this memory special?'
          value={values.richText}
          onChange={(_event: any, value: any) => setFieldValue('richText', value)}
        />
      </Grid>

      {open && (
        <MediaPopup
          open={open}
          onClose={handleClose}
          handleDeleteMedia={() => handleDeleteMedia(selectedMedia?.media)}
          handleFeaturedSelect={() => handleFeaturedSelect(selectedMedia?.media)}
          mediaSrc={selectedMedia || ''}
          index={selectedMedia?.media?.preview}
          isFeatured={selectedMedia?.media?.isFeatured || false}
          isVideo={
            selectedMedia?.type?.startsWith('video') ||
            (selectedMedia?.media?.url && getFileType(selectedMedia?.media?.url) == 'video') ||
            false
          }
        />
      )}
    </Box>
  );
};

export default MediaUploadStep;
